import { normalizeId } from './ids';

/**
 * WHAT A RULE MAY FILL A LOOKUP WITH (keepr-api KPR-1104; the owner's ruling of
 * 2026-10-21, "refuse text into lookup").
 *
 * A rule's write is not judged by anybody's read, so a value it writes into a
 * card-lookup element (or a repeating group's lookup column) must come from a
 * source nobody types: a LINK the rule copies (`{{trigger.<lookup>}}`,
 * `{{each.<lookup>}}`, `{{entry.<lookup column>}}`, one step through a lookup
 * to another lookup), a RECORD's own id (`{{trigger.id}}`, `{{each.id}}`), the
 * records the manager CHOSE (ids), or nothing. A text, a number, the clock or
 * any other element is refused by the API — 400 `lookup_source_refused` at
 * save, and a rule stored before fails that action at run.
 *
 * The API is the authority (keepr-api utils/automations/lookupSources.js). This
 * module is what the rule editor needs to OFFER only those sources, and to say,
 * on a rule saved before, which field the API would refuse: it reads the same
 * cards the editor already draws its pickers from, and gives NO verdict on a
 * token it cannot resolve (a hop through a card this client has not loaded) —
 * the save decides that one. It is not vendored: it judges by the editor's
 * definitions, never by a record.
 *
 * The SHAPE is the API's: ONE token on its own (whitespace around it, no date
 * offset), or a static value of one id or a list of ids.
 */

const HEX_ID_RE = /^[0-9a-f]{24}$/i;
// A token with no offset; and every token, offsets included (the API's two).
const PLAIN_TOKEN_RE = /\{\{\s*([A-Za-z0-9_.$-]+?)\s*\}\}/g;
const ANY_TOKEN_RE = /\{\{\s*([A-Za-z0-9_.$-]+?)\s*(?:[+-]\s*\d+\s*[dwm])?\s*\}\}/g;
// The fields an item token reads that are not an element.
const ITEM_FIELDS = new Set( [ 'id', 'card', 'title', 'createdAt', 'updatedAt', 'updatedBy' ] );
const LOOKUP = 'card-lookup';

const labelOf = el => el?.label?.singular || el?.name || '';
const isLookup = el => el?.dataType === LOOKUP;

/** The LAST definition of `name` in a merged element list (a descendant's restatement wins). */
const declared = ( elements, name ) => {
	let found = null;
	( elements || [] ).forEach( el => { if ( el?.name === name ) found = el; } );
	return found;
};

/** A value's parts, or null: the bases of a template made of tokens alone. */
export function tokensAlone( value ) {
	if ( typeof value !== 'string' || ! value.includes( '{{' ) ) return null;
	const bases = [];
	const rest = value.replace( PLAIN_TOKEN_RE, ( whole, base ) => { bases.push( base ); return ' '; } );
	const all = ( value.match( ANY_TOKEN_RE ) || [] ).length;
	if ( ! bases.length || ! /^[\s,]*$/.test( rest ) || bases.length !== all ) return null;
	return bases;
}

/**
 * What a stored value IS, for a lookup: `{ kind: 'blank' }`, `{ kind: 'records',
 * ids }` (one id, or a list of them), `{ kind: 'link', token }` (ONE token on
 * its own), or `{ kind: 'other', reason }` — `not_an_id` (a static value that
 * is not one id or a list of ids: several ids typed into one string are stored
 * as one string, which no lookup can hold) or `not_one_source` (a token beside
 * other text or another token, or with an offset: several tokens render into
 * one comma-joined string).
 */
export function parseLookupValue( value ) {
	if ( value === undefined || value === null ) return { kind: 'blank' };
	if ( Array.isArray( value ) ) {
		const ids = value.map( v => `${ v ?? '' }`.trim() );
		if ( ! ids.length ) return { kind: 'blank' };
		return ids.every( id => HEX_ID_RE.test( id ) ) ? { kind: 'records', ids } : { kind: 'other', reason: 'not_an_id' };
	}
	const text = `${ value }`;
	if ( ! text.trim() ) return { kind: 'blank' };
	if ( ! text.includes( '{{' ) ) return HEX_ID_RE.test( text.trim() ) ? { kind: 'records', ids: [ text.trim() ] } : { kind: 'other', reason: 'not_an_id' };
	const bases = tokensAlone( text );
	return bases && bases.length === 1 && ! text.includes( ',' ) ? { kind: 'link', token: `{{${ bases[ 0 ] }}}` } : { kind: 'other', reason: 'not_one_source' };
}

/**
 * The value to store for records chosen: one id as itself, several as a list
 * (a list stays a list on the way to the API; ids joined into one string would
 * be one string, which no lookup holds).
 */
export const recordsValue = ( ids, multiple ) => {
	const list = ( Array.isArray( ids ) ? ids : [ ids ] ).map( id => `${ id ?? '' }`.trim() ).filter( Boolean );
	if ( multiple ) return list;
	return list[ 0 ] || '';
};

/**
 * One step, then possibly another, through lookups from `elements` (the
 * trigger's): → 'link' when the walk ends on a lookup, 'record' when it ends on
 * `id`, null when it ends on anything else, and undefined when a card on the way
 * cannot be read here (no verdict).
 */
function walkKind( elements, segments, getElementsForCard ) {
	let defs = elements;
	for ( let i = 0; i < segments.length; i++ ) {
		const seg = segments[ i ];
		if ( i === segments.length - 1 ) {
			if ( seg === 'id' ) return 'record';
			if ( ITEM_FIELDS.has( seg ) ) return null;
			const el = declared( defs, seg );
			if ( ! el ) return defs.length ? null : undefined;
			return isLookup( el ) ? 'link' : null;
		}
		const el = declared( defs, seg );
		if ( ! el ) return defs.length ? null : undefined;
		if ( ! isLookup( el ) ) return null;
		const target = normalizeId( el?.options?.lookupCardId );
		const next = target && getElementsForCard ? getElementsForCard( target ) : null;
		if ( ! Array.isArray( next ) || ! next.length ) return undefined;
		defs = next;
	}
	return null;
}

/**
 * What one token base is, as a source for a lookup: 'link' | 'record' | null,
 * or undefined (no verdict here). `ctx`: { triggerElements, eachElements,
 * entryColumns, getElementsForCard } — `eachElements` only inside a fan-out (or
 * a generator, whose `{{each.*}}` is the source), `entryColumns` only on a rule
 * that watches entries.
 */
export function tokenKind( base, ctx = {} ) {
	if ( typeof base !== 'string' ) return null;
	const dot = base.indexOf( '.' );
	if ( dot < 0 ) return null; // {{today}}, {{now}}
	const ns = base.slice( 0, dot );
	const rest = base.slice( dot + 1 );
	const single = ! rest.includes( '.' );
	if ( ns === 'trigger' ) {
		if ( ! ctx.triggerElements ) return null;
		if ( single ) {
			if ( rest === 'id' ) return 'record';
			if ( ITEM_FIELDS.has( rest ) && ! ( rest === 'updatedBy' && declared( ctx.triggerElements, rest ) ) ) return null;
			return isLookup( declared( ctx.triggerElements, rest ) ) ? 'link' : null;
		}
		return walkKind( ctx.triggerElements, rest.split( '.' ), ctx.getElementsForCard );
	}
	if ( ns === 'each' ) {
		// A fan-out whose rows a typed value picks (steeringToken): what each
		// row holds is then that person's choice, never a source.
		if ( ! ctx.eachElements || ! single || ctx.eachSteeredBy ) return null;
		if ( rest === 'id' ) return 'record';
		if ( ITEM_FIELDS.has( rest ) && ! ( rest === 'updatedBy' && declared( ctx.eachElements, rest ) ) ) return null;
		return isLookup( declared( ctx.eachElements, rest ) ) ? 'link' : null;
	}
	if ( ns === 'entry' ) {
		if ( ! ctx.entryColumns || ! single ) return null;
		return isLookup( declared( ctx.entryColumns, rest ) ) ? 'link' : null;
	}
	// A card action's pressed value (keepr-api KPR-382): a card-lookup INPUT is
	// a record the presser picked, so it feeds a lookup; any other input does not.
	if ( ns === 'input' ) {
		if ( ! Array.isArray( ctx.inputs ) || ! single ) return null;
		return ctx.inputs.some( input => input?.key === rest && input?.dataType === LOOKUP ) ? 'link' : null;
	}
	return null;
}

/**
 * The API's verdict on a stored value bound for a lookup, as far as this client
 * can tell: null (allowed, or not known here), or `{ reason, token? }` —
 * `not_an_id`, `not_tokens_alone`, or `not_a_link` with the token.
 */
export function lookupValueRefusal( value, ctx = {} ) {
	const parsed = parseLookupValue( value );
	if ( parsed.kind === 'other' ) return { reason: parsed.reason };
	if ( parsed.kind !== 'link' ) return null;
	const base = parsed.token.slice( 2, -2 ).trim();
	if ( tokenKind( base, ctx ) !== null ) return null;
	if ( base.startsWith( 'each.' ) && ctx.eachSteeredBy ) return { reason: 'steered', token: parsed.token, by: ctx.eachSteeredBy };
	return { reason: 'not_a_link', token: parsed.token };
}

/**
 * WHO PICKS A FAN-OUT'S ROWS (keepr-api KPR-1104, the review's finding 1): the
 * first token of a query (`forEach` via kql) that is not a link, a record's own
 * id or the clock — a value a person typed, which then chooses the record
 * `{{each.id}}` names — as `{{base}}`, or null. A token this client cannot
 * judge (a hop through a card it has not loaded) is no verdict.
 */
export function steeringToken( kql, ctx = {} ) {
	if ( typeof kql !== 'string' || ! kql.includes( '{{' ) ) return null;
	const outer = { ...ctx, eachElements: null, eachSteeredBy: null };
	for ( const m of kql.matchAll( ANY_TOKEN_RE ) ) {
		const base = m[ 1 ];
		if ( base === 'today' || base === 'now' ) continue;
		if ( tokenKind( base, outer ) === null ) return `{{${ base }}}`;
	}
	return null;
}

/** Is `cardId` the lookup's card or one beneath it (a record of a child card is a record of its parent)? */
function holds( targetCardId, cardId, cardsById ) {
	const target = normalizeId( targetCardId );
	if ( ! target ) return true;
	let cursor = normalizeId( cardId );
	const seen = new Set();
	while ( cursor && ! seen.has( cursor ) ) {
		if ( cursor === target ) return true;
		seen.add( cursor );
		cursor = normalizeId( cardsById?.[ cursor ]?.parentCardId );
	}
	return false;
}

/**
 * THE SOURCES THE EDITOR OFFERS for a lookup `target`, grouped for a Select:
 * the item that set the rule off (itself, its lookups, and one step through
 * each of them to another lookup), each matching item (itself and its lookups,
 * in a fan-out or a generator), and the entry (its lookup columns). Only a
 * source whose records the lookup can hold — its card or one beneath it — is
 * offered. Each option's value is the token the API stores; its label is words.
 */
export function lookupSourceOptions( {
	target,
	triggerElements = null,
	triggerCardId = '',
	triggerCardName = '',
	eachElements = null,
	eachCardId = '',
	eachCardName = '',
	eachSteered = false,
	entryColumns = null,
	entryLabel = '',
	// A card action's inputs: a card-lookup input whose card the lookup can hold.
	inputs = null,
	getElementsForCard,
	cardsById,
} = {} ) {
	const targetCard = normalizeId( target?.options?.lookupCardId );
	const fits = cardId => holds( targetCard, cardId, cardsById );
	const lookupsOf = elements => {
		const byName = new Map();
		( elements || [] ).forEach( el => { if ( el?.name ) byName.set( el.name, el ); } );
		return [ ...byName.values() ].filter( isLookup );
	};
	const groups = [];
	if ( triggerElements ) {
		const options = [];
		if ( triggerCardId && fits( triggerCardId ) ) options.push( { value: '{{trigger.id}}', label: `The ${ triggerCardName || 'item' } itself` } );
		lookupsOf( triggerElements ).forEach( el => {
			if ( fits( el?.options?.lookupCardId ) ) options.push( { value: `{{trigger.${ el.name }}}`, label: labelOf( el ) } );
			const next = getElementsForCard ? getElementsForCard( normalizeId( el?.options?.lookupCardId ) ) : null;
			lookupsOf( next ).forEach( hop => {
				if ( fits( hop?.options?.lookupCardId ) ) options.push( { value: `{{trigger.${ el.name }.${ hop.name }}}`, label: `${ labelOf( el ) } · ${ labelOf( hop ) }` } );
			} );
		} );
		if ( options.length ) groups.push( { label: 'From the item that set it off', options } );
	}
	if ( eachElements && ! eachSteered ) {
		const options = [];
		if ( eachCardId && fits( eachCardId ) ) options.push( { value: '{{each.id}}', label: `Each ${ eachCardName || 'matching item' } itself` } );
		lookupsOf( eachElements ).forEach( el => {
			if ( fits( el?.options?.lookupCardId ) ) options.push( { value: `{{each.${ el.name }}}`, label: labelOf( el ) } );
		} );
		if ( options.length ) groups.push( { label: 'From each matching item', options } );
	}
	if ( entryColumns ) {
		const options = lookupsOf( entryColumns )
			.filter( col => fits( col?.options?.lookupCardId ) )
			.map( col => ( { value: `{{entry.${ col.name }}}`, label: labelOf( col ) } ) );
		if ( options.length ) groups.push( { label: entryLabel ? `From the ${ entryLabel } entry` : 'From the entry', options } );
	}
	if ( Array.isArray( inputs ) ) {
		const options = inputs
			.filter( input => input?.key && input?.dataType === LOOKUP && fits( input?.options?.lookupCardId ) )
			.map( input => ( { value: `{{input.${ input.key }}}`, label: String( input.label || '' ).trim() || input.key } ) );
		if ( options.length ) groups.push( { label: 'Asked when pressed', options } );
	}
	return groups;
}

/**
 * A token in words, for the sentence beneath a refused field: "Ward on the item
 * that set it off", "Today", "Note on the entry". Never the token itself.
 */
export function describeToken( token, ctx = {} ) {
	const base = `${ token || '' }`.replace( /^\{\{\s*|\s*\}\}$/g, '' ).replace( /\s*[+-]\s*\d+\s*[dwm]$/, '' );
	const [ ns, ...path ] = base.split( '.' );
	if ( ns === 'today' ) return 'Today\'s date';
	if ( ns === 'now' ) return 'The time now';
	if ( ns === 'slot' ) return 'The occurrence';
	if ( ns === 'watch' ) return 'The value that crossed';
	if ( ns === 'input' ) {
		const asked = ( ctx.inputs || [] ).find( input => input?.key === path[ 0 ] );
		return asked?.label ? `${ asked.label } asked when pressed` : 'A pressed value';
	}
	const names = ( elements, list ) => {
		let defs = elements || [];
		return list.map( ( name, i ) => {
			const el = declared( defs, name );
			if ( el && i < list.length - 1 && ctx.getElementsForCard ) defs = ctx.getElementsForCard( normalizeId( el?.options?.lookupCardId ) ) || [];
			return el ? labelOf( el ) : ( { id: 'Id', title: 'Title', card: 'Card', createdAt: 'Created', updatedAt: 'Updated', updatedBy: 'Last saved by' }[ name ] || name );
		} ).join( ' · ' );
	};
	if ( ns === 'trigger' ) return `${ names( ctx.triggerElements, path ) } on the item that set it off`;
	if ( ns === 'each' ) return `${ names( ctx.eachElements, path ) } on each matching item`;
	if ( ns === 'entry' ) return `${ names( ctx.entryColumns, path ) } on the entry`;
	return 'A value typed into the rule';
}

/** The sentence a refused field says, in words (the API's words, without its syntax). */
export function refusalSentence( verdict, ctx = {} ) {
	if ( ! verdict ) return '';
	const lead = 'A lookup can only be filled from a link or a chosen record';
	if ( verdict.reason === 'not_a_link' ) return `${ lead }, and ${ describeToken( verdict.token, ctx ).replace( /^./, c => c.toLowerCase() ) } is not one. Pick a source from the list, or choose a record.`;
	if ( verdict.reason === 'steered' ) return `${ lead }, and here ${ describeToken( verdict.by, ctx ).replace( /^./, c => c.toLowerCase() ) }, a typed value, picks the matching items. Pick another source, or choose a record.`;
	if ( verdict.reason === 'not_one_source' ) return `${ lead }, and this value mixes one with other text or another source. Pick one source from the list, or choose records.`;
	return `${ lead }, and this value is typed text. Choose a record, or pick a source from the list.`;
}

export const LOOKUP_SOURCE_REFUSED = 'lookup_source_refused';
